import * as THREE from 'three'

export type FighterMeshIntegrityIssue={
 mesh:string
 code:'missing-skin-attributes'|'skin-index-out-of-range'|'foreign-skeleton-bone'|'invalid-bind-matrix'
 detail:string
}
export type FighterMeshIntegrityReport={
 skinnedMeshes:number
 rigidMeshes:number
 bones:number
 quarantinedMeshes:number
 issues:FighterMeshIntegrityIssue[]
}

const finiteMatrix=(m:THREE.Matrix4)=>m.elements.every(Number.isFinite)
const meshName=(mesh:THREE.Object3D)=>mesh.name||mesh.uuid

function maxSkinIndex(attribute:THREE.BufferAttribute){
 let max=-1
 const values=attribute.array as ArrayLike<number>
 for(let i=0;i<values.length;i++)if(Number.isFinite(Number(values[i])))max=Math.max(max,Number(values[i]))
 return max
}
function cloneMaterials(mesh:THREE.Mesh){
 const material=mesh.material
 mesh.material=Array.isArray(material)?material.map(item=>item.clone()):material.clone()
}

export function stabilizeFighterMeshOwnership(root:THREE.Object3D):FighterMeshIntegrityReport{
 const ownedBones=new Set<THREE.Bone>()
 let rigidMeshes=0,bones=0,skinnedMeshes=0,quarantinedMeshes=0
 const issues:FighterMeshIntegrityIssue[]=[]
 root.traverse(node=>{
  if((node as THREE.Bone).isBone){ownedBones.add(node as THREE.Bone);bones++}
 })
 root.traverse(node=>{
  if(!(node instanceof THREE.Mesh))return
  cloneMaterials(node)
  // Only two fighters are rendered at once; avoid stale animated bounds culling cloth/gear.
  node.frustumCulled=false
  if(!(node instanceof THREE.SkinnedMesh)){rigidMeshes++;return}
  skinnedMeshes++
  const skinIndex=node.geometry.getAttribute('skinIndex') as THREE.BufferAttribute|undefined
  const skinWeight=node.geometry.getAttribute('skinWeight') as THREE.BufferAttribute|undefined
  const localIssues:FighterMeshIntegrityIssue[]=[]
  if(!skinIndex||!skinWeight)localIssues.push({
   mesh:meshName(node),code:'missing-skin-attributes',detail:'SkinnedMesh is missing skinIndex/skinWeight',
  })
  else{
   const maxIndex=maxSkinIndex(skinIndex)
   if(maxIndex>=node.skeleton.bones.length)localIssues.push({
    mesh:meshName(node),code:'skin-index-out-of-range',
    detail:`max skin index ${maxIndex} >= skeleton bones ${node.skeleton.bones.length}`,
   })
  }
  const foreign=node.skeleton.bones.filter(bone=>!ownedBones.has(bone))
  if(foreign.length)localIssues.push({
   mesh:meshName(node),code:'foreign-skeleton-bone',detail:`${foreign.length} bone(s) outside cloned fighter root`,
  })
  if(!finiteMatrix(node.bindMatrix)||!finiteMatrix(node.bindMatrixInverse))localIssues.push({
   mesh:meshName(node),code:'invalid-bind-matrix',detail:'bind matrix contains non-finite values',
  })
  if(localIssues.length){
   // Contain a broken clothing/gear/body submesh instead of letting bad skinning explode the whole fighter.
   node.visible=false
   node.userData.inyronMeshQuarantined=true
   quarantinedMeshes++
   issues.push(...localIssues)
  }else node.userData.inyronMeshQuarantined=false
 })
 const report={skinnedMeshes,rigidMeshes,bones,quarantinedMeshes,issues}
 root.userData.inyronMeshIntegrity=report
 return report
}

export function fighterMeshIntegrityGreen(report:FighterMeshIntegrityReport){
 return report.quarantinedMeshes===0&&report.issues.length===0
}
